/**
 * The data layer for the three P2 control screens — New case, Case, Queue.
 *
 * P2 built eight guards and, until the runner landed, none of them had a
 * consumer: on 23 August the platform generated its first live draft **over a
 * blocking `DEADLINE_MISMATCH`** because the driver walked the stages by hand
 * and read none of their answers (`artifacts/run_log.json`, and the runner's
 * own docstring). These screens are the second consumer, and their whole job is
 * the reading — so this module transcribes what the run record says and derives
 * as little as it can get away with.
 *
 * Three rules follow from that, and they are the reason this file exists rather
 * than each screen calling {@link api} directly:
 *
 * - **`exportable` is the server's word (I4).** It is a field on the response
 *   and it is never recomputed here from `verdict`, from `gate_findings`, or
 *   from anything else. A screen that decided for itself what was sendable
 *   would be a second gate, and the second gate is always the lenient one.
 * - **Both deadline dates travel together (I7).** {@link DeadlineReconciliation}
 *   carries `printed_date` and `computed_date` as separate fields and there is
 *   no helper here that collapses them into one — the entry on the papers is
 *   one of two sources, never the only one.
 * - **A run is created, never mutated.** There is no PATCH: a second opinion is
 *   a second run, so the runs list is an append-only history of what was asked
 *   and what came back (I12's shape, applied to a resource the log does not own).
 *
 * The matters endpoints live here too rather than in an `api/matters.ts` of
 * their own. The three screens are one feature area — start a run, watch a run,
 * list the matters that have runs — and a matter with no run to look at is not
 * something any of them can show. When a screen outside this set needs matters,
 * that is the moment to split the module, not before.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'

/**
 * Tones are imported as a *type* — erased at build, so this stays a data module
 * with no component dependency. They live beside the vocabulary they describe
 * for the reason `Badge.tsx` gives for keeping `GateBadge` next to `Badge`: a
 * BLOCK that is red on one screen and grey on another is a flag nobody trusts.
 */
import type { BadgeTone } from '@/components/ui'

import { API_BASE, ApiError, api, parseErrorBody } from './client'
import type {
  GateSeverity,
  Jurisdiction,
  Matter,
  MatterCreateRequest,
  MatterDetail,
  MatterLedger,
  PipelineRun,
  PipelineRunRequest,
  PipelineRunSummary,
  PipelineVerdict,
  RunLedger,
  SignResult,
  StageOutcome,
  WorklistAction,
} from './types'

/**
 * A timestamp on screen has exactly one definition, and it already exists.
 *
 * It lives in `admin.ts` because that is where the first table needed it;
 * moving it to a shared module is a rename in another agent's file, and a
 * second copy of six lines here would be the worse of the two problems.
 */
export { formatWhen } from './admin'

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * The stage spine, in the order the runner walks it, with a one-line gloss for
 * each in plain words (the Guide's own wording for the same steps). The Case
 * screen prints it under the stage's name, so it must read to someone who has
 * never seen the pipeline's internals: no engineering vocabulary here.
 *
 * `s0_intake` and `s1b_marks` are in the vocabulary but not in `RUN_ORDER`:
 * intake is the upload door itself and marks are a parallel lane no stage of
 * this sequence reads. They are listed so that a run which one day reports them
 * still renders with a label rather than a bare identifier.
 */
export const STAGE_LABELS: Record<string, string> = {
  s0_intake: 'Receives the file and spots a duplicate upload',
  s1_parse: 'Checks there is readable text; reads a scanned page by OCR',
  s1b_marks: 'Finds the trade marks on the papers',
  s2_normalize: 'Works out the grounds and the response deadline',
  s3_analyze: 'Turns the objection into points the response must meet',
  s4_research: 'Looks up provisions, decided cases and argument patterns',
  s5_generate: 'Writes the draft; skipped on a checks-only run',
  s6_review: 'Checks the draft against fixed rules and lists what it refuses',
}

/**
 * The six retrieval placeholders in prompt order
 * (`server/app/drafting/bundle.py`'s `RETRIEVED_PLACEHOLDERS`).
 *
 * Used to *order* the bundle card, never to filter it: a block the server sends
 * under a name this list does not know is still rendered, at the end. Dropping
 * it would be the one failure mode this card exists to prevent — a draft that
 * is thin for a reason nobody can see.
 */
export const RETRIEVAL_BLOCK_ORDER: readonly string[] = [
  'arguments',
  'authorities',
  'law',
  'phrases',
  'flow',
  'stance',
]

/**
 * A real ground for each office, for the New case form's placeholder.
 *
 * These four are the plan's own per-office coverage probes
 * (docs/IMPLEMENTATION_PLAN.md §7: `US 2(d) · IN 11(1) · EM 8(1)(b) · GB 5(2)(b)`),
 * so the example a person is shown is a string the corpus actually answers to.
 */
export const GROUND_EXAMPLES: Record<Jurisdiction, string> = {
  US: '2(d)',
  GB: '5(2)(b)',
  EM: '8(1)(b)',
  IN: '11(1)',
}

/** How a verdict is drawn, and what it means, in the runner's own terms. */
export interface VerdictCopy {
  tone: BadgeTone
  gloss: string
}

/**
 * The five ends, glossed.
 *
 * `declined` is deliberately not red. A decline is a **terminal pass state**
 * (plan §5 P2 item 13): the pipeline recognised a document it cannot serve
 * honestly and said which one, which is the system working. Drawing it as a
 * failure would teach people to treat an honest refusal as a bug to route
 * around, and routing around it is how a non-English matter gets a draft.
 */
export const VERDICT_COPY: Record<PipelineVerdict, VerdictCopy> = {
  drafted: {
    tone: 'ok',
    gloss: 'A draft exists and the gate did not block it. The only exportable end.',
  },
  blocked: {
    tone: 'block',
    gloss:
      'There is a draft, and one of the checks refuses to let it be sent. It is kept so you can read what was written — but nothing can send it until the check is cleared.',
  },
  declined: {
    tone: 'neutral',
    gloss:
      'The pipeline refused this matter and named the reason. Terminal, and a pass rather than a failure: the platform answered honestly instead of drafting over a document it cannot read.',
  },
  held: {
    tone: 'warn',
    gloss:
      'The reading is ambiguous and a person must look. Not a decline — a decline names a reason, and this is exactly the case that cannot.',
  },
  not_built: {
    tone: 'outline',
    gloss:
      'A stage this matter needs is a port that has not landed. Neither a pass nor a failure: the status line names the file that will implement it.',
  },
}

/** BLOCK stops export, WARN goes to the worklist, NOTE is an observation. */
export const SEVERITY_TONE: Record<GateSeverity, BadgeTone> = {
  BLOCK: 'block',
  WARN: 'warn',
  NOTE: 'neutral',
}

/** What a stage did, in the palette the mockup fixed: red = do not proceed. */
export const OUTCOME_TONE: Record<StageOutcome, BadgeTone> = {
  passed: 'ok',
  noted: 'warn',
  stopped: 'block',
  not_built: 'outline',
  skipped: 'neutral',
}

/** How long between polls while a run is still live. */
const RUN_POLL_MS = 2_000

/**
 * Is this run still worth polling?
 *
 * `status === 'running'` alone is not the question, and treating it as the
 * question is the bug this helper exists to close. Persist-first commits the
 * run row *before* the slow work, so a request that dies mid-run — a closed
 * tab, a proxy timeout, a restart — leaves a row saying `running` that nothing
 * will ever come back to update. Polling on `status` alone spins that row every
 * two seconds for as long as the screen is open.
 *
 * The server decides when a run has outlived any honest runtime and reports it
 * as `abandoned` on every read. A run is live when the record says running and
 * the server still believes it.
 */
export function isRunLive(run: { status: string; abandoned: boolean } | undefined): boolean {
  return run?.status === 'running' && !run.abandoned
}

/** Tone per worklist action. STOP is the only one that refuses an export. */
export const ACTION_TONE: Record<WorklistAction, BadgeTone> = {
  STOP: 'block',
  VERIFY: 'warn',
  SUPPLY: 'accent',
  CONSIDER: 'outline',
}

/** What each action means, above the lines that carry it. */
export const ACTION_COPY: Record<WorklistAction, string> = {
  STOP: 'Export is refused until these are resolved. Every one is a deterministic gate rule or a blocking deadline — not a judgement.',
  VERIFY: 'Check each against a source. The draft may be right; nobody has confirmed it yet.',
  SUPPLY: 'Only the client has these. No amount of drafting substitutes for knowing them.',
  CONSIDER: 'Judgement calls. The draft is sendable without acting on them.',
}

/**
 * Roughly how long the worklist says this run will take, said honestly.
 *
 * The server sends `minutes_are_calibrated: false` and will keep doing so
 * until `lib/review_card.py`'s real timings land. Rendering "45 min" with no
 * qualifier would turn an estimate into a measurement on the way to the
 * screen, which is the I9 failure this codebase keeps finding in its own
 * output — so the qualifier is part of the formatter, not left to each caller
 * to remember.
 */
export function formatWorklistEffort(worklist: {
  total_minutes: number
  minutes_are_calibrated: boolean
}): string {
  const minutes = worklist.total_minutes
  const span =
    minutes < 60
      ? `${minutes} min`
      : `${Math.floor(minutes / 60)}h ${minutes % 60 ? `${minutes % 60}m` : ''}`.trim()
  return worklist.minutes_are_calibrated ? span : `~${span} (estimated, not measured)`
}

/**
 * Why this run did or did not call a model.
 *
 * `generated` is a single boolean and the screens were reading it as if it
 * answered two questions. It answers one: *was a completion actually spent*.
 * It is recorded from a counting wrapper around the provider, deliberately —
 * "the caller asked for generation" and "the provider was invoked" are
 * different facts and I7 is only checkable if the record keeps them apart
 * (`services/pipeline_runs.py`).
 *
 * So `generated === false` covers four quite different runs, and adversarial
 * review found the Case screen calling all of them a dry run:
 *
 * * one that asked for a draft and was stopped at the deadline reconciliation
 *   before s5 — told "This was a dry run … Turn on Generate a draft", which is
 *   advice to switch on a toggle that was already on, for a run the platform
 *   refused on purpose;
 * * one still in flight, where nothing is decided yet;
 * * one whose process vanished, where the spend is genuinely unknown and the
 *   abandoned notice on the same screen says so — contradicted two cards down;
 * * and an actual dry run.
 *
 * The discriminator is the stage strip, which the API already sends. A dry run
 * is the one case where the pipeline *reached* s5 and found no generator: the
 * runner records `s5_generate` with outcome `skipped` and returns `not_built`
 * (`runner.py`, `if generate is None`). A run stopped earlier has no
 * `s5_generate` stage at all, and its `status_line` names where it stopped.
 */
export type ModelCallState = 'called' | 'in_flight' | 'unknown' | 'dry_run' | 'stopped_before'

export function modelCallState(run: {
  generated: boolean
  status: string
  abandoned: boolean
  stages: readonly { stage: string; outcome: string }[]
}): ModelCallState {
  if (run.generated) return 'called'
  // Order matters: an abandoned row is also `running`, and "we cannot know" is
  // a stronger and more honest statement than "not yet".
  if (run.abandoned) return 'unknown'
  if (run.status === 'running') return 'in_flight'
  const s5 = run.stages.find((stage) => stage.stage === 's5_generate')
  return s5?.outcome === 'skipped' ? 'dry_run' : 'stopped_before'
}

/** One sentence per state, for the Verdict card's "Model call" row. */
export const MODEL_CALL_COPY: Record<ModelCallState, string> = {
  called: 'yes — a completion was spent',
  in_flight: 'not yet — the run is still going',
  unknown:
    'unknown — the process that would have counted it never came back. Check the provider’s log before assuming this run was free.',
  dry_run: 'no — a dry run: every guard ran and no generator was supplied',
  stopped_before:
    'no — the run stopped before it reached the drafting step, so nothing was spent. The line above says where it stopped.',
}

/**
 * The badge on one row of a run list.
 *
 * A summary carries no stage strip, so {@link modelCallState}'s four-way answer
 * is not available here — "reached s5 and found no generator" and "stopped
 * before s5" are indistinguishable from a list row. Rather than guess, the
 * label says only what `generated` actually means. The old wording was "dry
 * run", which is a guess, and wrong for every run the platform refused.
 *
 * `status` is rendered beside it because a list row that shows neither the
 * status nor the abandoned flag draws a crashed run as a blank line with a
 * duration — the run picker had a failed run rendering as an empty
 * `status_line` (a failed run has none) next to "60m 0s".
 */
export const RUN_ROW_LABELS = {
  called: 'model call',
  not_called: 'no model call',
} as const

/**
 * Why there is no draft, in the run's own terms.
 *
 * Deliberately never suggests turning the toggle on except in the one case
 * where that is actually the answer. Telling somebody to ask for a draft on a
 * run the platform refused — an unreconciled deadline, a declined document —
 * invites them to do it again and read the second refusal as a bug.
 */
export const NO_DRAFT_COPY: Record<ModelCallState, string> = {
  called:
    'A model call was made and returned nothing usable — the run record’s error line above is what the runner recorded.',
  in_flight: 'The run is still going. If a draft comes back, it appears here.',
  unknown:
    'This run never came back, so whether it reached a model is not recorded. Nothing is stored as a draft either way — start a new run when you need this matter answered.',
  dry_run:
    'This was a dry run: every guard ran, no model was called, and no draft was expected. Turn on “Generate a draft” on New case to spend a model call.',
  stopped_before:
    'The pipeline stopped before it reached the drafting stage, so no model was called and no draft was expected. The status line below says what stopped it — that is the answer to act on, not a missing draft.',
}

/**
 * What an abandoned run says on screen.
 *
 * Deliberately not "failed". Nothing about the pipeline went wrong — the
 * process holding the run disappeared, and what it had spent by then is not
 * knowable from this side. A run drawn as a pipeline failure would send an
 * operator looking for a bug in the drafting stages that is not there, so the
 * copy names the request as the thing that vanished and says the cost is
 * unknown rather than implying it was nothing.
 */
export const ABANDONED_COPY = {
  label: 'abandoned',
  headline: 'This run never came back',
  detail:
    'The request that started it died before the pipeline returned — a closed tab, a proxy timeout, or a restart. The row was written first precisely so this would be visible instead of silently lost. Whether it reached a model before it vanished is not recorded: the process that would have counted the completions is the one that went away, so check the provider’s log before assuming it was free. Start a new run when you need this matter answered.',
} as const

// ---------------------------------------------------------------------------
// Request bodies — pure, so a test can read them without a server
// ---------------------------------------------------------------------------

/**
 * Trim, drop blanks, and de-duplicate the grounds a person typed.
 *
 * Order is preserved because the first ground is the one the stance block is
 * built for (`bundle.py`'s `ground_for_stance`), so "which came first" is a
 * decision the analyst made and not one to re-sort away. Case is *not*
 * normalised: `11(1)` and `11(1) ` differ by an accident, `2(d)` and `2(D)`
 * might not, and guessing which is which belongs to extraction, not to a form.
 */
export function cleanGrounds(values: readonly string[]): string[] {
  const seen = new Set<string>()
  const kept: string[] = []
  for (const value of values) {
    const ground = value.trim()
    if (!ground || seen.has(ground)) continue
    seen.add(ground)
    kept.push(ground)
  }
  return kept
}

/** What the New case form holds before it is a request. */
export interface RunDraft {
  documentText: string
  grounds: string[]
  office: Jurisdiction | null
  kind: string | null
  docRole: string
  reportDate: string
  printedDeadline: string
  caseFacts: string
  generate: boolean
  variant: string
  maxRetries: number
}

/**
 * Build the `POST .../pipeline/runs` body.
 *
 * Exported and pure for the same reason `admin.ts`'s key builders are: the
 * difference between an empty string and `null` in `printed_deadline` is the
 * difference between "the papers printed nothing" and "here is a date", and I7
 * turns that into whether a two-source cross-check happened at all. A test
 * asserts the null; it is not left to be discovered in a server log.
 */
export function pipelineRunBody(draft: RunDraft): PipelineRunRequest {
  return {
    document_text: draft.documentText,
    grounds: cleanGrounds(draft.grounds),
    office: draft.office,
    kind: draft.kind?.trim() ? draft.kind.trim() : null,
    doc_role: draft.docRole.trim() || 'response',
    report_date: draft.reportDate || null,
    printed_deadline: draft.printedDeadline || null,
    case_facts: draft.caseFacts,
    generate: draft.generate,
    variant: draft.variant,
    max_retries: draft.maxRetries,
  }
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const pipelineKeys = {
  matters: ['matters'] as const,
  matter: (matterId: string) => ['matters', matterId] as const,
  runs: (matterId: string) => ['matters', matterId, 'pipeline-runs'] as const,
  run: (runId: string) => ['pipeline-runs', runId] as const,
  ledger: (runId: string) => ['pipeline-runs', runId, 'ledger'] as const,
  matterLedger: (matterId: string) => ['matters', matterId, 'ledger'] as const,
}

// ---------------------------------------------------------------------------
// Matters
// ---------------------------------------------------------------------------

/**
 * The docket, newest first.
 *
 * `limit` is bounded here rather than left to the server's default of 50
 * because the Queue asks for one run list per row (see {@link useLatestRun}),
 * so the page's request count is this number plus one.
 */
export function useMatters(limit = 25): UseQueryResult<Matter[]> {
  return useQuery<Matter[]>({
    queryKey: pipelineKeys.matters,
    queryFn: () => api.get<Matter[]>('/api/matters', { query: { limit } }),
  })
}

/** One matter with its append-only event log (I12). */
export function useMatter(matterId: string | null): UseQueryResult<MatterDetail> {
  return useQuery<MatterDetail>({
    queryKey: pipelineKeys.matter(matterId ?? ''),
    queryFn: () => api.get<MatterDetail>(`/api/matters/${encodeURIComponent(matterId ?? '')}`),
    enabled: Boolean(matterId),
  })
}

export function useCreateMatter() {
  const queryClient = useQueryClient()
  return useMutation<Matter, unknown, MatterCreateRequest>({
    mutationFn: (body) => api.post<Matter>('/api/matters', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.matters })
    },
  })
}

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

/** Every run on one matter, newest first. The history of what was asked. */
export function usePipelineRuns(matterId: string | null): UseQueryResult<PipelineRunSummary[]> {
  return useQuery<PipelineRunSummary[]>({
    queryKey: pipelineKeys.runs(matterId ?? ''),
    queryFn: () =>
      api.get<PipelineRunSummary[]>(
        `/api/matters/${encodeURIComponent(matterId ?? '')}/pipeline/runs`,
      ),
    enabled: Boolean(matterId),
  })
}

/**
 * One run, in full.
 *
 * Polls while the run is *live* and stops the moment it is not. A generate run
 * makes a real model call and takes 30–60s, so the alternative to polling is a
 * screen that looks finished and is not — and the run record is the only place
 * the answer exists, since nothing streams.
 *
 * Live is {@link isRunLive}, not `status === 'running'`. A run whose request
 * died leaves a `running` row that nothing will update, and polling it on
 * status alone never stops.
 */
export function usePipelineRun(runId: string | null): UseQueryResult<PipelineRun> {
  return useQuery<PipelineRun>({
    queryKey: pipelineKeys.run(runId ?? ''),
    queryFn: () => api.get<PipelineRun>(`/api/pipeline/runs/${encodeURIComponent(runId ?? '')}`),
    enabled: Boolean(runId),
    // `isRunLive`, not `status === 'running'`: an abandoned row keeps that
    // status until the next write to the run table closes it, and polling on
    // the status alone is what spun a dead run forever.
    refetchInterval: (query) => (isRunLive(query.state.data) ? RUN_POLL_MS : false),
  })
}

/**
 * The run's citation ledger — every `bundle.source_ids` entry resolved back
 * to a document (I2 / REQ-21), read live off `GET
 * /api/pipeline/runs/{run_id}/ledger` rather than derived from `run.bundle`
 * here: the stored bundle keeps only the id list, and `resolve_source` is
 * the one place "does this link open" is decided.
 *
 * Does not poll. Unlike {@link usePipelineRun} there is nothing live about a
 * ledger — the source rows it resolves do not change while a run sits on
 * screen, and a second fetch answers the same thing the first one did.
 */
export function useRunLedger(runId: string | null): UseQueryResult<RunLedger> {
  return useQuery<RunLedger>({
    queryKey: pipelineKeys.ledger(runId ?? ''),
    queryFn: () =>
      api.get<RunLedger>(`/api/pipeline/runs/${encodeURIComponent(runId ?? '')}/ledger`),
    enabled: Boolean(runId),
  })
}

/**
 * Chunk 9's cross-run sibling — every source any of a matter's runs cited,
 * deduplicated, each entry naming which run(s) it came from
 * (`MatterLedgerEntry.run_ids`). A matter with no runs yet answers with an
 * empty ledger (200, not 404) — see the router's own reasoning for why that
 * is a fact to show rather than an error to throw.
 */
export function useMatterLedger(matterId: string | null): UseQueryResult<MatterLedger> {
  return useQuery<MatterLedger>({
    queryKey: pipelineKeys.matterLedger(matterId ?? ''),
    queryFn: () =>
      api.get<MatterLedger>(`/api/matters/${encodeURIComponent(matterId ?? '')}/ledger`),
    enabled: Boolean(matterId),
  })
}

/**
 * Start a run (I1: this is the expensive side of the confirm boundary).
 *
 * Invalidates the matter's run list and the matter itself — the stages append
 * events and can project lifecycle, so the log on screen must not go stale
 * behind a run that just wrote to it.
 */
export function useStartPipelineRun() {
  const queryClient = useQueryClient()
  return useMutation<PipelineRun, unknown, { matterId: string; body: PipelineRunRequest }>({
    mutationFn: ({ matterId, body }) =>
      api.post<PipelineRun>(`/api/matters/${encodeURIComponent(matterId)}/pipeline/runs`, body),
    onSuccess: (_run, { matterId }) => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.runs(matterId) })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.matter(matterId) })
    },
  })
}

/**
 * Correct the deadline on a matter (I1's `facts.corrected`).
 *
 * Human-only server-side, and deliberately so: `deadline_date` is one of the
 * confirm-before-compute facts, and a machine rewriting it after a person
 * confirmed it is the failure I1 exists to stop. The hook cannot make an
 * API-key session pass that gate — it can only surface the 403.
 *
 * The projection clears `deadline_verified` when the date actually moves
 * (`_clear_verification_if_date_moves`), so correcting a signed deadline
 * un-signs it rather than leaving an old signature reading as valid for a date
 * it was never given for. That is why this invalidates the matter.
 */
export function useCorrectDeadline() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, { matterId: string; deadlineDate: string; note?: string }>({
    mutationFn: ({ matterId, deadlineDate, note }) =>
      api.post(`/api/matters/${encodeURIComponent(matterId)}/events`, {
        stage: 's3_analyze',
        event: 'facts.corrected',
        payload: {
          deadline_date: deadlineDate,
          // What a person searching the log a year later needs: the date alone
          // says what changed and nothing about why.
          note: note || 'corrected on the case screen',
        },
      }),
    onSuccess: (_result, { matterId }) => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.matter(matterId) })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.matters })
      // The Queue too, and this one was missed. Moving a deadline is the ONE
      // correction that changes what the Queue shows about a matter twice over:
      // the SLA clock is computed from the date, and the server clears
      // `deadline_verified` whenever the date moves. Without this the Queue
      // kept counting down to the old date beside a signature that no longer
      // existed — the two sibling hooks below both invalidate it.
      void queryClient.invalidateQueries({ queryKey: ['queue'] })
    },
  })
}

/**
 * The REQ-1 attorney signature on the deadline — `deadline.verified`.
 *
 * `CaseScreen` used to say "P3 builds that control, so it is not a button
 * here", and the caution was right: a button that LOOKED like the signature
 * and was not would be the worst thing on that screen. This is that control,
 * and it is the real event — human-only at the server
 * (`HUMAN_ONLY_EVENTS`), append-only, and it is what
 * `services/pack.py` reads before it will approve anything for dispatch.
 */
export function useVerifyDeadline() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, { matterId: string; deadlineDate: string | null }>({
    mutationFn: ({ matterId, deadlineDate }) =>
      api.post(`/api/matters/${encodeURIComponent(matterId)}/events`, {
        stage: 'system',
        event: 'deadline.verified',
        payload: {
          verified: true,
          // The date being attested to, recorded in the event itself. The
          // projection only sets a boolean, so without this the log would say
          // somebody signed and not what they signed.
          deadline_date: deadlineDate,
        },
      }),
    onSuccess: (_result, { matterId }) => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.matter(matterId) })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.matters })
      void queryClient.invalidateQueries({ queryKey: ['queue'] })
    },
  })
}

/**
 * Record a decision to proceed on a deadline that has already passed.
 *
 * **Why this exists at all**, when the block is right by default: a lapsed
 * deadline is not always a lost one. An extension is ordinary — a USPTO office
 * action offers a three-month one in as many words — restoration is available
 * in several regimes, and a matter under a granted extension is not late, the
 * platform simply does not know about the extension. A demonstration or a
 * benchmark over a fixed set of documents also decays into un-runnability as
 * its dates pass.
 *
 * **Why it is not a setting.** A global "allow lapsed deadlines" would serve
 * the same cases in one line, and is exactly how a genuinely lapsed deadline
 * ships to a client. This costs a named person a deliberate act on one named
 * matter, with a reason, in the append-only log. The server refuses it to a
 * machine credential (403 `human_required`) like every other event a numbered
 * invariant reserves to a person.
 *
 * **What it does not do.** It does not correct the date — if an extension moved
 * the deadline, edit it, and this is not the tool. It does not sign the
 * deadline (REQ-1 is a separate act). And it clears exactly one flag:
 * `DEADLINE_ALREADY_PASSED`. A mismatch between the papers and the rule is a
 * disagreement about what the date *is*, which this decision says nothing about
 * and which still stops the matter.
 */
export function useOverrideDeadline() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, { matterId: string; reason: string }>({
    mutationFn: ({ matterId, reason }) =>
      api.post(`/api/matters/${encodeURIComponent(matterId)}/events`, {
        stage: 'system',
        event: 'override.recorded',
        payload: {
          // Scoped, because `override.recorded` is a general vocabulary word
          // and only the deadline half is built. A gate override written later
          // must not be read as a decision about a date.
          scope: 'deadline',
          reason,
        },
      }),
    onSuccess: (_result, { matterId }) => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.matter(matterId) })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.matters })
      void queryClient.invalidateQueries({ queryKey: ['queue'] })
    },
  })
}

/**
 * End a matter a person has decided not to take further.
 *
 * **Not a delete.** Nothing in this application deletes a matter — the event
 * log refuses UPDATE and DELETE by database trigger, and the matter row is
 * ON DELETE RESTRICT behind it. The server's own route docstring says the rest.
 *
 * **Not a decline either.** The four decline reasons each name a document the
 * pipeline could not serve honestly, and DECLINED counts as a *pass* in the
 * queue and the metrics. A person deciding a matter is finished is a different
 * act and gets its own door.
 *
 * The reason is required by the server, not just by this form: a matter that
 * stopped with nobody able to say why is the state this whole endpoint exists
 * to replace.
 */
export function useCloseMatter() {
  const queryClient = useQueryClient()
  return useMutation<Matter, unknown, { matterId: string; reason: string }>({
    mutationFn: ({ matterId, reason }) =>
      api.post<Matter>(`/api/matters/${encodeURIComponent(matterId)}/close`, { reason }),
    onSuccess: (_matter, { matterId }) => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.matter(matterId) })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.matters })
      void queryClient.invalidateQueries({ queryKey: ['queue'] })
    },
  })
}

/**
 * Attorney sign-off (chunk 7) — human-only server-side (`current_human`);
 * this hook cannot make an API-key session pass that gate, only surface the
 * 403 `human_required` the server already answers with.
 *
 * Invalidates the run so `usePipelineRun`'s next read shows `signed: true`
 * without a manual refetch — the same reason {@link useStartPipelineRun}
 * invalidates the runs list.
 */
export function useSignRun() {
  const queryClient = useQueryClient()
  return useMutation<SignResult, unknown, string>({
    mutationFn: (runId) =>
      api.post<SignResult>(`/api/pipeline/runs/${encodeURIComponent(runId)}/sign`),
    onSuccess: (_result, runId) => {
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.run(runId) })
    },
  })
}

/**
 * Render a signed run's letterhead `.docx` and hand it back as a file.
 *
 * Bypasses {@link api} on purpose: `apiFetch` decodes every non-JSON body
 * with `response.text()`, which corrupts binary content, and this is the one
 * endpoint in the product that answers with bytes rather than JSON. The
 * filename is read off `Content-Disposition` — the server names it once
 * (`server/app/services/sign.py`'s `ExportOutcome.filename`) rather than this
 * hook reconstructing it from the run id.
 */
export async function exportPipelineRun(runId: string): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(
    `${API_BASE}/api/pipeline/runs/${encodeURIComponent(runId)}/export`,
    { method: 'POST', credentials: 'include' },
  )
  if (!response.ok) {
    const body = await response.json().catch(() => undefined)
    const { code, message, issues } = parseErrorBody(response.status, body)
    throw new ApiError({
      status: response.status,
      code,
      message,
      issues,
      body,
      method: 'POST',
      url: response.url,
    })
  }
  const disposition = response.headers.get('content-disposition') ?? ''
  const match = /filename="?([^";]+)"?/.exec(disposition)
  const filename = match?.[1] ?? `${runId}.docx`
  const blob = await response.blob()
  return { blob, filename }
}

/** {@link exportPipelineRun} as a mutation, for a button's loading/error state. */
export function useExportRun() {
  return useMutation<{ blob: Blob; filename: string }, unknown, string>({
    mutationFn: (runId) => exportPipelineRun(runId),
  })
}

/**
 * The newest run on a matter, for a Queue row.
 *
 * One request per row, and that is a deliberate cost rather than an oversight:
 * the API contract has no runs-across-matters endpoint, and the alternative —
 * showing a docket with no verdict column — hides exactly the thing the Queue
 * exists to show. `undefined` while it loads, `null` when the matter has never
 * been run, which are different states and are drawn differently.
 */
export function useLatestRun(matterId: string): {
  run: PipelineRunSummary | null | undefined
  isPending: boolean
  isError: boolean
} {
  const runs = usePipelineRuns(matterId)
  return {
    run: runs.data ? (runs.data[0] ?? null) : undefined,
    isPending: runs.isPending,
    isError: runs.isError,
  }
}

// ---------------------------------------------------------------------------
// Formatting shared by the run screens
// ---------------------------------------------------------------------------

/** A run's wall-clock cost, or an em dash while it is still running. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—'
  if (ms < 1_000) return `${ms} ms`
  const seconds = ms / 1_000
  if (seconds < 60) return `${seconds.toFixed(1)} s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${Math.round(seconds - minutes * 60)}s`
}

/** An ISO date as it is written on the papers, or an em dash for "no date". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const when = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(when.getTime())) return iso
  return when.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
}

/** A stage's gloss, or the identifier itself when the vocabulary grew. */
export function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] ?? stage
}

/**
 * The bundle's blocks in prompt order, with unknown names kept at the end.
 *
 * Returns every entry the server sent. An empty block is a row with a count of
 * zero, never a missing row (`bundle.py`: an empty block is "a *fact about this
 * draft* that the reviewer needs").
 */
export function orderedBlocks<T>(blocks: Record<string, T>): Array<[string, T]> {
  const known = RETRIEVAL_BLOCK_ORDER.filter((name) => name in blocks).map(
    (name) => [name, blocks[name] as T] as [string, T],
  )
  const extra = Object.keys(blocks)
    .filter((name) => !RETRIEVAL_BLOCK_ORDER.includes(name))
    .map((name) => [name, blocks[name] as T] as [string, T])
  return [...known, ...extra]
}
